import {
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import clsx from 'clsx';
import {
  type CSSProperties,
  type JSX,
  type ReactNode,
  cloneElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { ContextualColor } from './colors';


export interface TooltipProps {
  children: JSX.Element;
  contents: ReactNode;
  position?: 'top' | 'left' | 'right' | 'bottom';
  color?: 'primary' | ContextualColor;
  maxWidth?: number;
  delayMs?: number;
  style?: CSSProperties;
}

/**
 * Use Tooltips to display additional information about the element that they appear on.
 *
 * The component uses @floating-ui/react, which makes sure the tooltip is visible by avoiding collisions.
 *
 * @see: https://developer.sgmarkets.com/docs/design-system/v5/components/tooltips.html
 *
 * @param children {ReactNode} Tooltip will appear
 *
 * @param contents {ReactNode} The contents of the tooltip
 *
 * @param position top, bottom, left, right
 *
 * @param color primary, info, success, warning, danger
 *
 * @param maxWidth Maximum width in pixel, overrides sg-bootstrap default
 *
 * @param maxWidth Maximum width in pixel, overrides sg-bootstrap default
 *
 * @param delayMs Waits for the specified time when the event listener runs before changing the open state.
 *
 * @param style Override style. Can override css variables
 *  - --bs-tooltip-bg
 *  - --bs-tooltip-color
 *  - --bs-tooltip-zindex
 *  - --bs-tooltip-max-width
 *  - --bs-tooltip-padding-x
 *  - --bs-tooltip-padding-y
 *  - --bs-tooltip-margin
 *  - --bs-tooltip-font-size
 *  - --bs-tooltip-border-radius
 *  - --bs-tooltip-opacity
 *  - --bs-tooltip-arrow-width
 *  - --bs-tooltip-arrow-height
 *
 */
export function Tooltip({
  children,
  contents,
  color,
  position = 'bottom',
  maxWidth,
  delayMs,
  style,
}: TooltipProps) {
  const [open, setOpen] = useState(false);

  const arrowRef = useRef<HTMLDivElement | null>(null);
  const {
    x,
    y,
    strategy,
    context,
    refs,
    update,
    middlewareData: {
      arrow: { x: arrowX, y: arrowY } = {},
    },
  } = useFloating({
    placement: position,
    open,
    onOpenChange: setOpen,
    middleware: [offset(10), flip(), shift({ padding: 8 }), arrow({ element: arrowRef })],
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, { delay: delayMs }),
    useFocus(context),
    useRole(context, { role: 'tooltip' }),
    useDismiss(context),
  ]);

  useEffect(() => {
    if (refs.reference.current && refs.floating.current && open) {
      return autoUpdate(refs.reference.current, refs.floating.current, update);
    }
  }, [refs.reference, refs.floating, update, open]);

  const maxSizeStyle = maxWidth !== undefined ? { '--bs-tooltip-max-width': maxWidth } : {};
  const tooltipStyle = {
    ...style,
    ...maxSizeStyle,
  };
  return (
    <>
      {cloneElement(children, getReferenceProps({ ref: refs.setReference, ...children.props }))}
      {open && (
        <div
          data-popper-placement={position}
          {...getFloatingProps({
            ref: refs.setFloating,
            className: clsx('tooltip fade bs-tooltip-auto show', color && [`tooltip-${color}`]),
            style: {
              position: strategy,
              top: y ?? '',
              left: x ?? '',
            },
          })}
        >
          <div
            className="tooltip-arrow"
            ref={arrowRef}
            style={{
              ...tooltipStyle,
              position: 'absolute',
              transform: `translate(${arrowX ?? 0}px, ${arrowY ?? 0}px)`,
            }}
          />
          <div className="tooltip-inner" style={tooltipStyle}>
            {contents}
          </div>
        </div>
      )}
    </>
  );
}
